import { getFirstEncodableAudioCodec, getFirstEncodableVideoCodec, type AudioCodec, type VideoCodec } from 'mediabunny';
import type { HardwareTierName } from '../../../shared/protocol.ts';

/**
 * Picks recording settings that this particular machine can sustain.
 * Local recording quality is the product, so we measure rather than guess: a short
 * synthetic encode tells us how much headroom the video encoder really has.
 */

export interface VideoPreset {
  width: number;
  height: number;
  fps: number;
  bitrate: number;
}

export interface HardwareProfile {
  tier: HardwareTierName;
  cores: number;
  memoryGb: number | null;
  webcodecs: boolean;
  videoCodec: VideoCodec | null;
  /** Codec for audio embedded in video files (the separate mic master is always WAV). */
  audioCodec: AudioCodec | null;
  /** Frames per second a 720p encode managed in the benchmark (higher = more headroom). */
  encodeFps720: number | null;
  /**
   * What three full-HD encodes at once managed together, in 720p 30 fps streams: the recording budget (quality.ts).
   * Hardware encoders work on several streams in parallel, so this is usually well above one stream's speed.
   */
  encodeUnits?: number | null;
  /**
   * Encoder latency mode for master recordings. 'quality' never drops frames and avoids Chrome-on-macOS's
   * slow first frame, but Safari's H.264 encoder emits nothing at all in 'quality' mode, so it is measured.
   */
  videoLatencyMode: 'quality' | 'realtime';
  /**
   * How much later than its input this browser's audio encoder labels its output, in seconds (diagnostics).
   * Chrome: 0. Safari: 16 ms with 256-frame input, which put the camera file's sound late until the recorder
   * started retiming packets by sample count (recorder.ts, retimeAudioPackets).
   */
  audioTimestampLagS: number;
  notes: string[];
  measuredAt: number;
}

const CACHE_KEY = 'takehouse.hardware.v6';

export function cachedProfile(): HardwareProfile | null {
  try {
    const p = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as HardwareProfile | null;
    if (p && Date.now() - p.measuredAt < 7 * 24 * 3600 * 1000) return p;
  } catch {}
  return null;
}

/** Used if the check itself fails: standard settings and H.264, which every supported browser encodes. */
export function fallbackProfile(): HardwareProfile {
  return {
    tier: 'standard',
    cores: navigator.hardwareConcurrency || 2,
    memoryGb: (navigator as any).deviceMemory ?? null,
    webcodecs: typeof VideoEncoder !== 'undefined',
    videoCodec: 'avc',
    audioCodec: 'aac',
    encodeFps720: null,
    videoLatencyMode: 'realtime', // works in every browser we've tested
    audioTimestampLagS: 0,
    notes: ['The hardware check failed; standard settings chosen.'],
    measuredAt: 0,
  };
}

export async function probeHardware(force = false): Promise<HardwareProfile> {
  if (!force) {
    const cached = cachedProfile();
    if (cached) return cached;
  }
  const notes: string[] = [];
  const cores = navigator.hardwareConcurrency || 2;
  const memoryGb = (navigator as any).deviceMemory ?? null;
  const webcodecs = typeof VideoEncoder !== 'undefined' && typeof AudioEncoder !== 'undefined';

  let videoCodec: VideoCodec | null = null;
  let audioCodec: AudioCodec | null = null;
  let encodeFps720: number | null = null;
  let encodeUnits: number | null = null;
  let videoLatencyMode: HardwareProfile['videoLatencyMode'] = 'quality';
  let audioTimestampLagS = 0;
  if (webcodecs) {
    // H.264 first: hardware-encoded almost everywhere and the friendliest format for editors.
    // Each step has a time limit: some browser/driver combinations never answer a test encode, and a
    // stuck check must not stop people getting to their camera and mic.
    videoCodec = await withTimeout(
      'video codec check',
      getFirstEncodableVideoCodec(['avc', 'vp9', 'av1', 'vp8'], { width: 1920, height: 1080, bitrate: 8e6 }),
      5000,
    ).catch(() => supportedVideoCodec());
    audioCodec = await withTimeout(
      'audio codec check',
      getFirstEncodableAudioCodec(['aac', 'opus'], { numberOfChannels: 2, sampleRate: 48000, bitrate: 192e3 }),
      5000,
    ).catch(() => supportedAudioCodec());
    if (audioCodec) {
      audioTimestampLagS = await withTimeout('audio timing check', audioTimestampLag(audioCodec), 3000).catch(() => 0);
      if (audioTimestampLagS) console.info(`[takehouse] audio encoder labels its output ${(audioTimestampLagS * 1000).toFixed(1)} ms late; packets are retimed`);
    }
    if (videoCodec) {
      const quality = await withTimeout('quality-mode encode check', encodesIn('quality', videoCodec), 4000).catch(() => false);
      videoLatencyMode = quality ? 'quality' : 'realtime';
      if (!quality) console.info('[takehouse] this encoder emits nothing in quality mode; recording in realtime mode');
      // Throughput is measured in quality mode (realtime paces output). Where High profile stalls in quality
      // mode (Safari), Baseline on the same hardware encoder gives the same answer.
      const benchString = quality ? CODEC_STRINGS[videoCodec] : videoCodec === 'avc' ? 'avc1.42002a' : CODEC_STRINGS[videoCodec];
      const benchMode = quality || videoCodec === 'avc' ? 'quality' : 'realtime';
      encodeFps720 = benchString
        ? await withTimeout('encoder benchmark', benchmark(benchString, benchMode), 10000).catch(() => null)
        : 60;
      if (benchString && encodeFps720) {
        encodeUnits = await withTimeout('parallel encoder benchmark', parallelBenchmark(benchString, benchMode), 10000).catch(() => null);
        if (encodeUnits) console.info(`[takehouse] three full-HD encodes at once: ${encodeUnits.toFixed(1)} × 720p 30 fps`);
      }
    }
    if (encodeFps720 == null && videoCodec) notes.push('The encoder speed test didn’t finish; standard settings chosen.');
    if (videoCodec !== 'avc') notes.push(`H.264 encoding unavailable; recording ${videoCodec?.toUpperCase()} instead.`);
  } else {
    notes.push('This browser lacks WebCodecs. Use a current Chrome, Edge, Safari or Firefox to record.');
  }

  let tier: HardwareTierName = 'standard';
  if (encodeFps720 != null) {
    // A 1080p30 camera + 1080p30 screen needs roughly 4x a single 720p30 stream of encode throughput.
    // The measured speed decides, not the core count: Safari reports at most 8 cores and iOS reports 4,
    // while their hardware encoders sit outside the CPU anyway.
    if (encodeFps720 >= 200 && cores >= 4) tier = 'high';
    else if (encodeFps720 >= 90 && cores >= 4) tier = 'standard';
    else tier = 'light';
  } else if (cores < 4) tier = 'light';
  if (memoryGb != null && memoryGb < 4 && tier === 'high') tier = 'standard';
  if (tier === 'light') notes.push('Lighter recording settings chosen to protect this machine’s encoder headroom.');

  const profile: HardwareProfile = {
    tier,
    cores,
    memoryGb,
    webcodecs,
    videoCodec,
    audioCodec,
    encodeFps720,
    encodeUnits,
    videoLatencyMode,
    audioTimestampLagS,
    notes,
    measuredAt: Date.now(),
  };
  saveProfile(profile);
  return profile;
}

/** Remembers the profile (including a quality the person picked by hand) for a week. */
export function saveProfile(profile: HardwareProfile) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(profile));
  } catch {}
}

export function withTimeout<T>(what: string, p: Promise<T>, ms: number): Promise<T> {
  const t0 = performance.now();
  let timer: ReturnType<typeof setTimeout>;
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      console.warn(`[takehouse] ${what} gave no answer within ${ms / 1000} s; carrying on without it`);
      reject(new Error(`${what} timed out`));
    }, ms);
  });
  return Promise.race([p, limit]).then(
    (v) => {
      clearTimeout(timer);
      console.info(`[takehouse] ${what}: ${Math.round(performance.now() - t0)} ms`);
      return v;
    },
    (e) => {
      clearTimeout(timer);
      throw e;
    },
  );
}

/** Fallback when a test encode hangs: ask the browser what it claims to support, without encoding. */
async function supportedVideoCodec(): Promise<VideoCodec | null> {
  for (const codec of ['avc', 'vp9', 'av1', 'vp8'] as VideoCodec[]) {
    const config = { codec: CODEC_STRINGS[codec]!, width: 1920, height: 1080, bitrate: 8e6 };
    const ok = await withTimeout('video codec support', VideoEncoder.isConfigSupported(config), 2000).catch(() => null);
    if (ok?.supported) return codec;
  }
  return null;
}

/**
 * Encodes a moment of silence in 256-frame blocks (as the recorder feeds it) and compares the first output
 * packet's timestamp with the first input's. Anything other than 0 is the encoder's labelling lag.
 */
async function audioTimestampLag(codec: AudioCodec): Promise<number> {
  const sampleRate = 48000;
  const firstInputUs = 1_000_000;
  let firstOutputUs: number | null = null;
  const enc = new AudioEncoder({
    output: (chunk) => {
      firstOutputUs ??= chunk.timestamp;
    },
    error: () => {},
  });
  enc.configure({ codec: codec === 'aac' ? 'mp4a.40.2' : 'opus', sampleRate, numberOfChannels: 1, bitrate: 128000 });
  for (let i = 0; i < 40; i++) {
    enc.encode(
      new AudioData({
        format: 'f32-planar',
        sampleRate,
        numberOfFrames: 256,
        numberOfChannels: 1,
        timestamp: firstInputUs + Math.round(((i * 256) / sampleRate) * 1e6),
        data: new Float32Array(256),
      }),
    );
  }
  await enc.flush();
  enc.close();
  if (firstOutputUs == null) return 0;
  const lag = (firstOutputUs - firstInputUs) / 1e6;
  // Only a plausible labelling offset counts (under 0.1 s, never negative).
  return lag > 0.0005 && lag < 0.1 ? lag : 0;
}

async function supportedAudioCodec(): Promise<AudioCodec | null> {
  const configs: [AudioCodec, string][] = [['aac', 'mp4a.40.2'], ['opus', 'opus']];
  for (const [codec, codecString] of configs) {
    const config = { codec: codecString, numberOfChannels: 2, sampleRate: 48000, bitrate: 192e3 };
    const ok = await withTimeout('audio codec support', AudioEncoder.isConfigSupported(config), 2000).catch(() => null);
    if (ok?.supported) return codec;
  }
  return null;
}

const CODEC_STRINGS: Partial<Record<VideoCodec, string>> = {
  avc: 'avc1.640028', // High profile, as mediabunny uses for recordings
  vp9: 'vp09.00.31.08',
  av1: 'av01.0.05M.08',
  vp8: 'vp8',
};

/** Does this encoder produce output in the given latency mode? A few small frames are enough to tell. */
async function encodesIn(mode: 'quality' | 'realtime', codec: VideoCodec): Promise<boolean> {
  const codecString = CODEC_STRINGS[codec];
  if (!codecString) return true;
  let outputs = 0;
  const encoder = new VideoEncoder({ output: () => outputs++, error: () => {} });
  try {
    encoder.configure({ codec: codecString, width: 640, height: 360, bitrate: 1e6, framerate: 30, latencyMode: mode });
    const canvas = new OffscreenCanvas(640, 360);
    const ctx = canvas.getContext('2d')!;
    for (let i = 0; i < 10; i++) {
      ctx.fillStyle = `hsl(${i * 30} 70% 50%)`;
      ctx.fillRect(0, 0, 640, 360);
      const frame = new VideoFrame(canvas, { timestamp: (i * 1e6) / 30 });
      encoder.encode(frame, { keyFrame: i === 0 });
      frame.close();
    }
    await Promise.race([encoder.flush(), new Promise((r) => setTimeout(r, 2000))]);
    return outputs > 0;
  } catch {
    return false;
  } finally {
    try {
      encoder.close();
    } catch {}
  }
}

/**
 * Encode 120 synthetic 720p frames as fast as the encoder accepts them; report throughput.
 * The test frames are drawn once up front: drawing and reading back a canvas for every frame is slow in Safari
 * and was being counted as encoder time, which put fast Macs and iPhones in a lower quality tier.
 */
async function benchmark(codecString: string, latencyMode: 'quality' | 'realtime', width = 1280, height = 720, frames = 120): Promise<number> {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d')!;
  const pictures: VideoFrame[] = [];
  for (let i = 0; i < 6; i++) {
    // Very different pictures in turn, so the encoder can't cheat with skip blocks.
    ctx.fillStyle = `hsl(${(i * 61) % 360} 70% 50%)`;
    ctx.fillRect(0, 0, width, height);
    for (let j = 0; j < 40; j++) {
      ctx.fillStyle = `hsl(${(i * 13 + j * 31) % 360} 80% 60%)`;
      ctx.fillRect((i * 97 + j * 53) % width, (j * 29 + i * 41) % height, 120, 80);
    }
    pictures.push(new VideoFrame(canvas, { timestamp: 0 }));
  }
  let outputs = 0;
  let resolveDone!: () => void;
  const done = new Promise<void>((r) => (resolveDone = r));
  const encoder = new VideoEncoder({
    output: () => {
      if (++outputs === frames) resolveDone();
    },
    error: () => resolveDone(),
  });
  let wake: (() => void) | null = null;
  encoder.addEventListener?.('dequeue', () => wake?.());
  try {
    // 'quality' mode: realtime mode paces output to the frame rate, which would hide the real headroom.
    encoder.configure({ codec: codecString, width, height, bitrate: Math.round(width * height * 30 * 0.145), framerate: 30, latencyMode });
    const t0 = performance.now();
    for (let i = 0; i < frames; i++) {
      const frame = new VideoFrame(pictures[i % pictures.length], { timestamp: (i * 1e6) / 30 });
      encoder.encode(frame, { keyFrame: i % 60 === 0 });
      frame.close();
      // Wait for room in the queue; 'dequeue' wakes us at once where supported, the timer is the fallback.
      while (encoder.encodeQueueSize > 4) await new Promise<void>((r) => ((wake = r), setTimeout(r, 4)));
    }
    await Promise.race([encoder.flush().then(() => resolveDone()), done, new Promise((r) => setTimeout(r, 6000))]);
    return Math.round(outputs / ((performance.now() - t0) / 1000));
  } finally {
    try {
      encoder.close();
    } catch {}
    for (const p of pictures) p.close();
  }
}

/** Three full-HD encodes at once, as a recording of a camera, a second camera and a screen runs them. */
async function parallelBenchmark(codecString: string, latencyMode: 'quality' | 'realtime'): Promise<number | null> {
  const fps = await Promise.all([0, 1, 2].map(() => benchmark(codecString, latencyMode, 1920, 1080, 90)));
  if (fps.some((f) => !f)) return null;
  // Frames per second at 1080p, as 720p 30 fps streams (a 1080p frame is 2.25 720p frames).
  return (fps.reduce((a, f) => a + f, 0) * 2.25) / 30;
}
